import { useCallback, useRef, useState } from 'react';

import type { FeedQuery, FeedQueryVariables } from '@/api/generated/graphql';
import { FEED_QUERY } from '@/api/queries/feed';
import { useAuthedClient } from '@/api/useAuthedClient';
import type { FeedItem } from '@/types/feed';

import { useGraphQLQuery } from './useGraphQLQuery';

interface UseFeedOptions {
  limit?: number;
  skip?: boolean;
}

interface UseFeedReturn {
  feedItems: FeedItem[];
  isLoading: boolean;
  error: Error | null;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  hasMore: boolean;
}

const DEFAULT_LIMIT = 20;

export function useFeed(options: UseFeedOptions = {}): UseFeedReturn {
  const { limit = DEFAULT_LIMIT, skip = false } = options;

  const authedClient = useAuthedClient();

  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef = useRef(0);
  const appendRef = useRef(false);

  const prevLimitRef = useRef(limit);
  const prevSkipRef = useRef(skip);
  if (prevLimitRef.current !== limit || (prevSkipRef.current && !skip)) {
    offsetRef.current = 0;
    appendRef.current = false;
  }
  prevLimitRef.current = limit;
  prevSkipRef.current = skip;

  const { isLoading, error, refetch } = useGraphQLQuery<FeedQuery>({
    fetcher: (signal) =>
      authedClient.request<FeedQuery, FeedQueryVariables>({
        document: FEED_QUERY,
        variables: { limit, offset: offsetRef.current },
        signal,
      }),
    deps: [limit, skip],
    enabled: !skip,
    onSuccess: (response) => {
      const append = appendRef.current;
      appendRef.current = false;
      const newItems = response.feed;
      setFeedItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(newItems.length === limit);
    },
  });

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    offsetRef.current += limit;
    appendRef.current = true;
    await refetch();
  }, [isLoading, hasMore, limit, refetch]);

  const wrappedRefetch = useCallback(async () => {
    offsetRef.current = 0;
    appendRef.current = false;
    await refetch();
  }, [refetch]);

  return {
    feedItems,
    isLoading,
    error,
    loadMore,
    refetch: wrappedRefetch,
    hasMore,
  };
}
